<template>
  <form
    class="form appointment-creation"
    autocomplete="off"
    v-on:reset="onClose"
    v-on:submit.prevent="onSubmit">
    <v-dialog
      v-model="isShownCanceledDialog"
      persistent
      content-class="appointment-creation__popup"
      max-width="fit-content">
      <v-card class="px-5 appointment-creation__popup-wrapper">
        <v-btn
          class="appointment-creation__popup__exit"
          icon
          v-on:click="isShownCanceledDialog = false">
          <v-icon color="#192a3e">mdi-close</v-icon>
        </v-btn>
        <v-card-title class="text-h7 justify-center">
          Отменить визит?
        </v-card-title>
        <autocomplete-input
          attach
          class="appointment-creation__patient"
          item-text="name"
          item-value="id"
          label="Причина отказа"
          placeholder="Выберите причину"
          v-bind:can-delete-items="true"
          v-bind:violations="rejectionReasonInputErrors"
          v-bind:items="[...rejectionReasonList, { name: 'Свой вариант', id: 'new' }]"
          v-model="rejectionReasonId"
          v-on:delete-item="onDeleteRejectionReason"
        />
        <input-text
          v-if="needsCreatingRejectionReason"
          v-bind:required="needsCreatingRejectionReason"
          title="Причина отказа"
          v-model="customRejectionReason"
          v-letter-capital
          v-bind:violations="customRejectionReasonInputErrors" />
        <textarea-length-limitation
          title="Комментарий"
          v-bind:max-length="100"
          rows="5"
          v-bind:violations="causeInputErrors"
          v-letter-capital
          v-model="cause" />
        <v-card-actions class="appointment-creation__popup__button-wrapper justify-center">
          <v-btn
            class="appointment-creation__popup__button-cancel"
            color="blue darken-1"
            text
            v-on:click="onCloseCancelAppointmentDialog">Нет</v-btn>
          <v-btn
            class="appointment-creation__popup__button-accept"
            text
            v-on:click="cancelAppointment">Да, отменить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="needsCreatingAdvertisingChannel"
      persistent
      content-class="appointment-creation__popup"
      max-width="fit-content">
      <v-card class="px-5 appointment-creation__popup-wrapper">
        <v-btn
          class="appointment-creation__popup__exit"
          icon
          v-on:click="onCloseCreatingAdvertisingChannelDialog">
          <v-icon color="#192a3e">mdi-close</v-icon>
        </v-btn>
        <v-card-title class="text-h7 justify-center">
          Добавление источника пациента
        </v-card-title>
        <input-text
          v-if="needsCreatingAdvertisingChannel"
          v-bind:required="needsCreatingAdvertisingChannel"
          title="Наименование"
          v-model="customAdvertisingChannel"
          v-letter-capital
          v-bind:violations="customAdvertisingChannelInputErrors" />
        <v-card-actions class="appointment-creation__popup__button-wrapper justify-center">
          <v-btn
            class="appointment-creation__popup__button-cancel"
            color="blue darken-1"
            text
            v-on:click="onCloseCreatingAdvertisingChannelDialog">Отмена</v-btn>
          <v-btn
            class="appointment-creation__popup__button-accept"
            text
            v-on:click="createAdvertisingChannel">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div
      v-if="initialEmployeeId"
      class="appointment-creation__datetime"
      v-bind:style="gapStyle">
      <div>
        <date-picker-dialog
          v-bind:value="initialDate"
          v-on:input="onInputDate"
          v-bind:allowed-dates="checkDayIsWork"
          v-on:change-month="onChangeMonth">
          <template v-slot:trigger="{ listenerObject }">
            <button
              v-if="!initialEmployeeId"
              class="appointment-creation__datetime__value"
              type="button"
              v-on="{}" />
            <button
              title="Сменить дату"
              v-on:click="onShowFailMessageEditDate"
              v-else-if="doesntHaveOpportunityToEditDate"
              class="appointment-creation__datetime__value__disabled"
              type="button"
            >{{ initialDateTitle }}</button>
            <button
              title="Сменить дату"
              v-else-if="isAllowedDate || isDoctorUnavailable"
              class="appointment-creation__datetime__value"
              v-bind:class="{ 'appointment-creation__datetime__value--error': isDoctorUnavailable }"
              type="button"
              v-on="listenerObject">{{ initialDateTitle }}</button>
            <button
              v-else
              class="appointment-creation__datetime__value"
              type="button"
              v-on="listenerObject">Укажите дату</button>
          </template>
        </date-picker-dialog>
        <div
          class="appointment-creation__datetime__error"
          v-for="(text, index) in dateInputErrors"
          v-bind:key="index">
          {{ text }}
        </div>
      </div>
      <div v-if="!isDoctorUnavailable">
        <popover-menu v-model="isShowAppointmentTimeRange">
          <template v-slot:trigger="{ listenerObject }">
            <button
              class="appointment-creation__datetime__value"
              title="Выбрать время"
              type="button"
              v-bind:disabled="isDisabledField"
              v-on="listenerObject">
              <template v-if="initialTimeStarts || initialTimeEnds">
                <time v-bind:datetime="startsAt">{{ initialTimeStarts }}</time>
                -
                <time v-bind:datetime="endsAt">{{ initialTimeEnds }}</time>
              </template>
              <template v-else>Задайте время</template>
            </button>
          </template>
          <form-appointment-time-range
            v-bind:initial-from="initialTimeStarts"
            v-bind:initial-to="initialTimeEnds"
            v-bind:preset-from-timeline="employeeTimeline.from"
            v-bind:preset-to-timeline="employeeTimeline.to"
            v-bind:timeline="[]"
            v-bind:presets-duration="presetsDuration"
            v-on:submit="onSubmitTime" />
        </popover-menu>
        <div
          class="appointment-creation__datetime__error"
          v-if="isTimeIncorrect && (initialTimeStarts || initialTimeEnds)">
          Некорректный временной промежуток
        </div>
        <div
          class="appointment-creation__datetime__error"
          v-for="(text, index) in timeInputErrors"
          v-bind:key="index">
          {{ text }}
        </div>
      </div>
      <button
        class="open-waiting-list"
        title="Перейти к листу ожидания"
        type="button"
        v-if="numWaitings"
        v-on:click="openWaitings">Лист ожидания ({{ numWaitings }})</button>
    </div>
    <div
      v-if="isOutdated"
      class="appointment-creation__outdated">
      Дата записи устарела
    </div>
    <div class="appointment-creation__fields">
      <dl class="appointment-creation__doctor">
        <dt>Врач:</dt>
        <autocomplete-input
          v-bind:disabled="isSaveDisabled || isCame || isDisabledField"
          class="appointment-creation__doctor__name"
          attach
          hide-details
          item-text="name"
          item-value="id"
          required="required"
          v-bind:items="employeeList"
          v-bind:value="initialEmployeeId"
          v-on:input="onInputEmployee"
          v-bind:violations="employeeErrors"
        />
        <button
          v-if="!isShowDoctorAssistant && hasChangeDoctorAssistant"
          class="appointment-creation__doctor__button add"
          type="button"
          title="Добавить врача-ассистента"
          v-on:click="onAddDoctorAssistant"
        />
      </dl>
      <dl v-if="isShowDoctorAssistant" class="appointment-creation__doctor">
        <dt>Врач-ассистент:</dt>
        <autocomplete-input
          v-bind:disabled="isSaveDisabled || !hasChangeDoctorAssistant"
          class="appointment-creation__doctor__name"
          attach
          hide-details
          item-text="name"
          item-value="id"
          required="required"
          v-bind:items="doctorAssistants"
          v-model="selectedDoctorAssistantId"
          v-bind:violations="employeeErrors"
        />
        <button
          v-if="hasChangeDoctorAssistant"
          class="appointment-creation__doctor__button remove"
          type="button"
          title="Удалить врача-ассистента"
          v-on:click="onRemoveDoctorAssistant"
        />
      </dl>
    </div>
    <div v-if="isDoctorUnavailable" class="appointment-creation__error-notification">
      Нет свободных слотов. Измените врача или дату приема.
    </div>
    <input-checkbox
      v-if="((needsCreatingPatient || !isNotInitialPatient) && !isEdit) || canEditInitial"
      key="is-second-checkbox"
      class="appointment-creation__checkboxes"
      title="Повторный"
      v-model="isSecond" />
    <input-checkbox
      class="appointment-creation__checkboxes"
      title="Новый пациент"
      v-if="canCreatePatient"
      v-model="needsCreatingPatient" />
    <div
      class="appointment-creation__row"
      v-if="needsCreatingPatient">
      <input-text
        required="required"
        title="Фамилия"
        v-bind:violations="surnameInputErrors"
        v-model="surname"
        v-letter-capital />
      <input-text
        required="required"
        title="Имя"
        v-bind:violations="nameInputErrors"
        v-model="name"
        v-letter-capital />
      <input-text
        title="Отчество"
        v-bind:violations="patronymicInputErrors"
        v-model="patronymic"
        v-letter-capital />
    </div>
    <div
      class="appointment-creation__row extra"
      v-if="needsCreatingPatient"
    >
      <input-typed-phone
        v-if="isShowPatientPhoneInput"
        class="appointment-creation__phone"
        title="Телефон"
        v-bind:violations="phoneInputErrors"
        v-bind:value="phone"
        v-bind:type="phoneType"
        v-on:input="onInputPhone"
      />
      <input-checkbox
        class="appointment-creation__checkboxes appointment-creation__mailing"
        title="Согласен на рассылку"
        v-model="agreeMailing" />
    </div>
    <input-date
      v-if="needsCreatingPatient"
      class="appointment-creation__date"
      label="Дата рождения"
      v-bind:violations="birthDateInputErrors"
      v-model="birthAt" />
    <template v-if="needsCreatingPatient && matchedPatients.length">
      <div class="appointment-creation__double">
        <div>
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="appointment-creation__double__title">
                  Внимание! Есть мед.карты с такими данными.
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <button
                  type="button"
                  v-for="patient in matchedPatients"
                  v-bind:key="`matched-by-name-and-birth-date-${patient.value}`"
                  v-on:click="setMatchedPatient(patient.value)"
                  class="appointment-creation__double__error"
                >
                  {{ patient.title }}
                  <span
                    v-if="patient.is_archived"
                    class="appointment-creation__double__icon-archive"
                    title="Пациент в архиве"
                  />
                </button>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </template>
    <autocomplete-input
      v-if="isShowPatientSelector"
      attach
      class="appointment-creation__patient multiline"
      hide-details
      item-text="text"
      item-value="id"
      label="ФИО пациента"
      required="required"
      v-bind:disable-search="true"
      v-bind:error="isPatientFieldInvalid"
      v-bind:items="patients"
      v-bind:search-input.sync="patientSearchQuery"
      v-bind:value="patientId"
      v-bind:disabled="isCame || isConfirmed"
      v-bind:keep-search-on-blur="true"
      v-on:blur="onBlurInputPatient"
      v-on:input="onInputPatient" />
    <autocomplete-input
      v-bind:disabled="isSaveDisabled"
      chips
      attach
      :key="`input-product-${autocompleteKey}`"
      class="appointment-creation__multiline"
      deletable-chips
      item-text="text"
      item-value="value"
      label="Выберите услугу"
      v-bind:multiple="true"
      v-bind:items="services"
      v-model="serviceIds"
      v-on:input="onSelectService"
      v-on:update:search-input="onUpdateServiceQuery" />
    <autocomplete-input
      v-bind:disabled="isSaveDisabled"
      attach
      class="appointment-creation__patient"
      item-text="name"
      item-value="id"
      label="Источник пациента"
      v-bind:required="isAdvertisingChannelRequired && needsCreatingPatient"
      placeholder="Выберите источник"
      v-bind:error-messages="advertisingChannelErrors"
      v-bind:items="[...advertisingChannels, { name: 'Добавить источник пациента', id: 'new' }]"
      v-model="advertisingChannelId"
    />
    <patient-labels
      class="appointment-creation__patient__labels"
      v-if="showPatientLabels"
      v-bind:labels="patientLabels"
    />
    <textarea-length-limitation
      v-bind:disabled="isSaveDisabled"
      placeholder="Примечание к приему"
      class="appointment-creation__textarea"
      rows="2"
      v-bind:max-length="$options.maxNoteLength"
      v-model="note"
      v-letter-capital />
    <div class="appointment-creation__waiting-list-notification" v-if="showNotification">
      <div>
        <img src="@/assets/img/nav/time.svg" alt="Ожидание">
        <a
          class="appointment-creation__waiting-list-link"
          v-bind:href="'/waiting-list?scroll_id=' + notification.id"
        >
          Пациент в листе ожидания
        </a>
      </div>
    </div>
    <div
      class="appointment-creation__buttons"
      v-bind:class="{ 'with-copy': hasCopy && isShowChangeStatusButtons }">
      <template v-if="isShowChangeStatusButtons">
        <button
          v-if="hasCopy"
          v-bind:disabled="isDisabledAppointmentCopyButton"
          v-on:click="onCopy"
          class="appointment-creation__button-copy"
          title="Копировать визит"
          type="button"
        />
        <button
          v-if="initialPatientId && hasViewPatientMedicalCard"
          class="appointment-creation__button appointment-creation__button-card"
          type="button"
          v-on:click="onPatientMedicalCard">В карту</button>
        <button
          v-if="initialPatientId && shownInvoicesButton"
          class="appointment-creation__button appointment-creation__button-invoices"
          type="button"
          v-on:click="onPatientInvoices">К счету</button>
        <button
          class="appointment-creation__button appointment-creation__button-cancel"
          type="button"
          v-on:click="isShownCanceledDialog = true">
          <img alt="Отменить визит" src="@/assets/img/exit-blue.svg">Отменить визит
        </button>
        <template v-if="showUpdateStatusButtons">
          <button
            v-if="isConfirmed"
            v-bind:disabled="disableStatusChange"
            class="appointment-creation__button-accept"
            type="button"
            v-on:click="updateStatus">
            <img
              src="@/assets/img/checked/all-checked-white-top.svg"
              alt="Пациент пришел">Пациент пришел</button>
          <button
            v-else
            v-bind:disabled="disableStatusChange"
            class="appointment-creation__button-accept"
            type="button"
            v-on:click="updateStatus">
            <img
              src="@/assets/img/checked/checked-white-top.svg"
              alt="Подтвердить визит">Подтвердить визит</button>
        </template>
      </template>
      <template v-else-if="isCame">
        <button
          v-if="initialPatientId && hasViewPatientMedicalCard"
          class="appointment-creation__button appointment-creation__button-card"
          type="button"
          v-on:click="onPatientMedicalCard">В карту</button>
        <button
          v-if="initialPatientId && shownInvoicesButton"
          class="appointment-creation__button appointment-creation__button-invoices"
          type="button"
          v-on:click="onPatientInvoices">К счету</button>
        <button
          v-if="hasViewPatientVisit"
          class="appointment-creation__button-visit"
          type="button"
          v-on:click="openVisit">К визиту</button>
        <button
          v-if="isShowCanceledAppointmentButton"
          class="appointment-creation__button appointment-creation__button-cancel"
          type="button"
          v-on:click="isShownCanceledDialog = true">
          <img alt="Отменить визит" src="@/assets/img/exit-blue.svg">Отменить визит
        </button>
      </template>
      <button
        v-if="!isCanceled"
        class="appointment-creation__button-save"
        type="submit"
        v-bind:title="submitButtonTitle"
        v-bind:disabled="isSaveDisabled">Сохранить</button>
      <template v-else>
        <button
          class="appointment-creation__button appointment-creation__button-cancel"
          type="button"
          v-on:click="onClose">Отмена</button>
        <button
          class="appointment-creation__button-save"
          type="button"
          v-bind:disabled="!isAllowedDate"
          v-on:click="onRestoreVisit">Восстановить</button>
      </template>
    </div>
    <v-expansion-panels
      v-if="hasViewVisitNotes"
      class="appointment-creation__notes appointment-notes"
    >
      <v-expansion-panel>
        <v-expansion-panel-header class="appointment-notes__header">
          <div class="appointment-notes__title">
            Заметки
            <span
              v-if="unreadVisitNotesCount > 0"
              class="appointment-notes__count"
              title='Количество новых заметок в визите'
            >({{ unreadVisitNotesCount }})</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="appointment-notes__content">
          <div class="appointment-notes__list">
            <div
              v-for="note in visitNotes"
              :key="note.id"
              class="appointment-notes__item"
              :class="{ 'appointment-notes__item--new': note.isNew }"
            >
              <div class="appointment-notes__item__content">
                <p>{{ note.text }}</p>
              </div>
              <div class="appointment-notes__item__info">
                <span class="appointment-notes__item__author">{{ note.author }}</span>
                <time class="appointment-notes__item__date">{{ note.date }}</time>
              </div>
            </div>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </form>
</template>
<script>
import {
  AppointmentExistPatientCreationRequest,
  AppointmentNewPatientCreationRequest,
} from '@/storage/gateways/AppointmentScheduleGateway';
import { GetMatchedPatientsRequest } from '@/storage/gateways/PatientGateway';
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputDate from '@/components/InputDate.vue';
import InputText from '@/components/InputText.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import dateMixin from '@/mixins/dateMixin';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';
import searchServiceMixin from '@/mixins/searchServiceMixin';
import { calcDifferenceMinutesForStringTime } from '@/mixins/appointmentMixin';
import debounce from 'lodash.debounce';
import PopoverMenu from '@/components/PopoverMenu.vue';
import FormAppointmentTimeRange from '@/components/FormAppointmentTimeRange.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import PatientLabels from '@/components/PatientLabels.vue';
import InputTypedPhone, { PHONE_TYPE_MOBILE } from '@/components/InputTypedPhone.vue';
import notifierMixin from '@/mixins/notifierMixin';
import searchPatientMixin from '@/mixins/searchPatientMixin';
import { GOAL_COPY_APPOINTMENT } from '@/plugins/metric';

/**
 * @description Пустой список ошибок.
 * @type {any[]}
 */
const EMPTY_INPUT_ERRORS = [];

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : EMPTY_INPUT_ERRORS
);

const NAME_EVENT_INPUT_DATE = 'input-date';
const NAME_EVENT_INPUT_EMPLOYEE = 'input-employee';
const NAME_EVENT_INPUT_MONTH = 'input-month';
const NAME_EVENT_MATCH_PATIENTS = 'match-patients';
const NAME_EVENT_FETCH_WAITING_PATIENT = 'check-waiting-patient';

/**
 * @description Название события удаления причины отказа.
 * @type {string}
 */
const NAME_EVENT_DELETE_REJECTION_REASON = 'delete-rejection-reason';

/**
 * @description Роли исключенные из просмотра кнопки "К счету".
 * @type {string[]}
 */
const EXCLUDED_CLINIC_ROLES_VIEW_INVOICES = [
  'doctor',
  'clinic-manager',
];

/**
 * @description Компонент формы создания записи на прием.
 * @type {Vue}
 */
export default {
  components: {
    AutocompleteInput,
    DatePickerDialog,
    FormAppointmentTimeRange,
    InputCheckbox,
    InputDate,
    InputText,
    InputTypedPhone,
    PatientLabels,
    PopoverMenu,
    TextareaLengthLimitation,
  },
  computed: {
    advertisingChannelErrors: resolveInputErrors('advertising-channel'),
    employeeErrors: resolveInputErrors('employee-id'),
    rejectionReasonInputErrors: resolveInputErrors('rejection-reason'),
    customAdvertisingChannelInputErrors: resolveInputErrors('custom-advertising-channel'),
    customRejectionReasonInputErrors: resolveInputErrors('custom-rejection-reason'),
    birthDateInputErrors: resolveInputErrors('birth-date'),
    causeInputErrors: resolveInputErrors('cause'),
    dateInputErrors: resolveInputErrors('date'),
    nameInputErrors: resolveInputErrors('name'),
    patronymicInputErrors: resolveInputErrors('patronymic'),
    phoneInputErrors: resolveInputErrors('phone'),
    surnameInputErrors: resolveInputErrors('surname'),
    timeInputErrors: resolveInputErrors('time'),
    needsCreatingRejectionReason() {
      return this.rejectionReasonId === 'new';
    },
    needsCreatingAdvertisingChannel() {
      return this.advertisingChannelId === 'new';
    },
    /**
     * @description Пациент первичный.
     * @returns {*}
     */
    isNotInitialPatient() {
      return this.patient?.visitCount !== 0 || this.patient?.notInitial;
    },
    /**
     * Нет возможности изменить дату
     *
     * @return {boolean}
     */
    doesntHaveOpportunityToEditDate() {
      if (!this.isSelectedAppointments) {
        return false;
      }
      return !this.hasChangeAppointmentDate
        || this.isDisabledField
        || this.initialDate < new Date();
    },
    doctorAssistantListWithInitial() {
      if (this.initialDoctorAssistant === null) {
        return this.doctorAssistantList;
      }
      const contains = this.doctorAssistantList.some(
        (candidate) => candidate.id === this.initialDoctorAssistant.id,
      );
      if (contains) {
        return this.doctorAssistantList;
      }
      return [...this.doctorAssistantList, this.initialDoctorAssistant];
    },
    doctorAssistants() {
      return this.doctorAssistantListWithInitial.filter(
        (candidate) => candidate.id !== this.initialEmployeeId,
      );
    },
    /**
     * @description Список рекламных записей.
     * @type {AdvertisingChannel[]}
     */
    advertisingChannels(vm) {
      const items = [];
      if (vm.patient && vm.patient.advertisingChannelId !== -1) {
        items.push(
          { id: vm.patient.advertisingChannelId, name: vm.patient.advertisingChannelName },
        );
      }
      if (vm.initialAdvertisingChannelId && vm.initialAdvertisingChannelId !== -1) {
        items.push({ id: vm.initialAdvertisingChannelId, name: vm.initialAdvertisingChannelName });
      }
      return items.length > 0
        ? [...vm.advertisingChannelList, ...items]
        : vm.advertisingChannelList;
    },
    /**
     * @description Сотрудник доступен для записи.
     * @type {Boolean}
     */
    isEmployeeAvailableForAppointment() {
      if (this.initialEmployeeId) {
        return !!this.employeeList.find(({ id }) => id === this.initialEmployeeId);
      }
      return false;
    },
    /**
     * Показывает есть ли записи в листе ожидания
     *
     * @returns {0|boolean}
     */
    showNotification() {
      return this.notification && this.patientId && !this.needsCreatingPatient;
    },
    gapStyle() {
      return this.initialEmployeeId ? {} : { gap: 0 };
    },
    /**
     * @description Возможность создать пациента.
     * @type {boolean}
     */
    canCreatePatient() {
      return !this.isEdit && this.canChangePatient && this.canCreatePatients;
    },
    /**
     * @description Деактивировать выбор рекламного канала.
     * @type {boolean}
     */
    disableAdvertisingChannelSelect({ patient }) {
      return patient && patient.advertisingChannelId > -1;
    },
    /**
     * @description Дата окончания.
     * @type {string}
     */
    endsAt(vm) {
      return `${vm.date} ${vm.initialTimeEnds}`;
    },
    /**
     * @description Заголовок источника пациента для существующей записи
     * @type {string}
     */
    selectedAdvertisingChannelTitleForExistedAppointment() {
      const advId = this.patient.advertisingChannelId;
      return this.disableAdvertisingChannelSelect && advId
        ? (this.advertisingChannels.find((item) => item.id === advId)).name
        : '';
    },
    /**
     * @description Дата начала.
     * @type {string}
     */
    startsAt(vm) {
      return `${vm.date} ${vm.initialTimeStarts}`;
    },
    /**
     * @description Отображать кнопку "К счету".
     * @returns {boolean}
     */
    shownInvoicesButton() {
      const allowed = !EXCLUDED_CLINIC_ROLES_VIEW_INVOICES.includes(this.$store.getters.getUserRole)
        && !this.$store.getters.isOwner;
      return this.hasViewPatientInvoices && allowed;
    },
    /**
     * @description Преобразованные в строку данные нового пациента.
     * @type {string}
     */
    stringifiedNewPatientData: (vm) => `${vm.name} ${vm.surname} ${vm.patronymic} ${vm.birthAt} ${vm.phone}`,
    isNameFilled() {
      return this.name?.length > 0
        && this.surname?.length > 0;
    },
    isPhoneValid: ({ phone }) => /^\+7[3-9][0-9]{9}$/.test(phone),
    initialDateTitle(vm) {
      const options = {
        day: 'numeric',
        month: 'long',
      };
      const weekday = vm.initialDate.toLocaleDateString('ru-RU', { weekday: 'short' });
      return `${vm.initialDate.toLocaleDateString('ru-RU', options)}, ${weekday.toUpperCase()}`;
    },
    isDisabledAppointmentCopyButton() {
      const isPatientFiled = this.needsCreatingPatient
        ? this.name.length > 0 && this.surname.length > 0
        : this.patientId !== null;
      return this.initialEmployeeId === null || isPatientFiled !== true;
    },
    yearAndMonth(vm) {
      const year = vm.initialDate.getFullYear();
      const month = vm.initialDate.getMonth() + 1;
      return String(year).concat('-', String(month).padStart(2, '0'));
    },
    /**
     * @description Выбранная дата визита доступна для записи.
     * @type {boolean}
     */
    isAllowedDate() {
      return this
        .employeeWorkDays
        .findIndex((item) => this.isoDate === item) !== -1;
    },
    /**
     * @description Выполняется редактирование.
     * @returns {boolean}
     */
    isEdit() {
      return this.appointmentId !== null;
    },
    /**
     * @description Текст при наведении на кнопку сохранения.
     * @type {string}
     */
    submitButtonTitle() {
      if (this.isShowAppointmentTimeRange) {
        return 'Выберите время';
      }
      return '';
    },
    /**
     * @description Деактивировать кнопку сохранения визита.
     * @type {boolean}
     */
    isSaveDisabled() {
      if (this.isAwaitRequest) {
        return true;
      }
      if (this.isTimeIncorrect && !this.isDoctorUnavailable) {
        return true;
      }
      if (this.isDoctorUnavailable) {
        return false;
      }
      const hasSave = (this.isEdit && this.actions.includes('edit')) || !this.isEdit;
      const isAvailable = hasSave
        && this.isAllowedDate
        && this.initialEmployeeId !== 0 ? this.isEmployeeAvailableForAppointment : true;
      return !isAvailable || this.isOutdated;
    },
    /**
     * @description Проверка врмени начала и конца на корректность.
     * @type {boolean}
     */
    isTimeIncorrect() {
      return !this.initialTimeStarts
      || !this.initialTimeEnds
      || this.initialTimeEnds <= this.initialTimeStarts;
    },
    /**
     * @description Показывать компонент выбора пациента.
     * @type {boolean}
     */
    isShowPatientSelector({ needsCreatingPatient, canChangePatient }) {
      return !needsCreatingPatient && canChangePatient;
    },
    /**
     * @description Имеет возможность изменять врача-ассистента.
     * @returns {boolean}
     */
    hasChangeDoctorAssistant() {
      return this.actions.includes('change-doctor-assistant');
    },
    /**
     * @description Имеет возможность изменять статус визита.
     * @returns {boolean}
     */
    hasChangeAppointmentStatus() {
      return this.actions.includes('change-appointment-status');
    },
    /**
     * @description Имеет возможность изменять дату визита.
     * @returns {boolean}
     */
    hasChangeAppointmentDate() {
      return this.actions.includes('change-appointment-date');
    },
    /**
     * @description Имеет возможность просмотра визита.
     * @returns {boolean}
     */
    hasViewVisitNotes() {
      return this.visitNotes.length > 0
        && this.$store.getters.getUserRole === 'doctor';
    },
    /**
     * @description Показывать поле ввода телефона пациента.
     * @returns {boolean}
     */
    isShowPatientPhoneInput() {
      return this.actions.includes('view-patient-phone');
    },
    /**
     * @description Отображать кнопки изменения статуса визита.
     * @type {boolean}
     */
    isShowChangeStatusButtons(vm) {
      return vm.appointmentId && !vm.isCame && vm.hasChangeAppointmentStatus && !vm.isCanceled;
    },
    /**
     * @description Визит отменен.
     * @type {boolean}
     */
    isCanceled() {
      return this.appointmentStatus === 'canceled';
    },
    /**
     * @description оказывать кнопку отмены визита.
     * @type {boolean}
     */
    isShowCanceledAppointmentButton() {
      return this.isCame && this.hasChangeAppointmentStatus;
    },
    /**
     * @description Визит состоялся.
     * @type {boolean}
     */
    isCame() {
      return this.appointmentStatus === 'came';
    },
    /**
     * @description Визит подтвержден.
     * @type {boolean}
     */
    isConfirmed() {
      return this.appointmentStatus === 'confirmed';
    },
    /**
     * @description Услуги визита.
     * @type {ServiceItem[]}
     */
    selectedServices({ initialServices, serviceIds, serviceList }) {
      return serviceIds.reduce((list, id) => {
        let service = serviceList.find((item) => item.value === id);
        if (service) {
          list.push(service);
        } else {
          service = initialServices.find((item) => item.value === id);
          if (service) {
            list.push(service);
          }
        }
        return list;
      }, []);
    },
    /**
     * @description Выбранный пациент.
     * @returns {any}
     */
    patient({ needsCreatingPatient, patientId, patients }) {
      if (needsCreatingPatient) {
        return null;
      }
      return patients.find((item) => item.id === patientId);
    },
    /**
     * @description Черты пациента.
     * @type {{id:string,name:string}[]}
     */
    patientLabels(vm) {
      if (vm.patient) {
        return vm.patient.labels;
      }
      return vm.initialPatientLabels;
    },
    /**
     * @description Отображать черты пациента.
     * @type {boolean}
     */
    showPatientLabels({ needsCreatingPatient, patientId }) {
      return !!patientId && patientId > 0 && needsCreatingPatient !== true;
    },
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields: (vm) => ({
      advertisingChannelId: vm.advertisingChannelId,
      birthAt: vm.birthAt,
      isoDate: vm.isoDate,
      employeeId: vm.initialEmployeeId,
      endsTimeAt: vm.initialTimeEnds,
      name: vm.name,
      note: vm.note,
      patientId: vm.patientId,
      patronymic: vm.patronymic,
      phone: vm.phone,
      phoneType: vm.phoneType,
      agreeMailing: vm.agreeMailing,
      serviceIds: vm.serviceIds,
      startsTimeAt: vm.initialTimeStarts,
      surname: vm.surname,
      patientSearchQuery: vm.patientId ? true : !!vm.patientSearchQuery,
    }),
    /**
     * @description Первичный прием
     * @returns {boolean}
     */
    isInitial() {
      if (this.canEditInitial) {
        return !this.isSecond;
      }
      return this.initialIsInitial || !this.isSecond;
    },
    /**
     * @description Дата в формате ISO.
     * @returns {string}
     */
    isoDate(vm) {
      return vm.formatDateToISO(vm.initialDate, true);
    },
    /**
     * @description Врач недоступен.
     * @returns {boolean}
     */
    isDoctorUnavailable() {
      if (!this.initialEmployeeId) {
        return false;
      }
      if (!this.isAllowedDate) {
        return true;
      }
      if (Array.isArray(this.employeeTimeline)) {
        return false;
      }
      return this.employeeTimeline.from && this.employeeTimeline.from.length === 0;
    },
    /**
     * @description Имя выбранного сотрудника.
     * @returns {string}
     */
    selectedEmployeeName() {
      const employee = this.employeeList.find((item) => item.id === this.initialEmployeeId);
      return employee ? employee.name : '';
    },
  },
  created() {
    if (this.isNeedFetchMatchedPatients()) {
      this.onMatchPatients();
    }
    if (this.isNeedFetchNotification()) {
      this.onFetchNotification(this.patientId);
    }
  },
  mounted() {
    if (this.patientId) {
      this.patientSearchQuery = this.patientFullName ? this.patientFullName : this.patientId;
    }
    this.fetchPatientList(this.patientSearchQuery);
  },
  data() {
    const serviceIds = this.initialServices.map(({ value }) => value);
    const isShowDoctorAssistant = this.initialDoctorAssistant !== null;
    const selectedDoctorAssistantId = isShowDoctorAssistant ? this.initialDoctorAssistant.id : null;
    return {
      /**
       * @description Показывать форму выбора времени начала и окончания записи на прием.
       * @type {boolean}
       */
      isShowAppointmentTimeRange: false,
      /**
       * @description Идентификатор рекламного канала.
       * @type {number}
       */
      advertisingChannelId: this.initialAdvertisingChannelId,
      /**
       * @description Отключено изменение статусов.
       * @type {boolean}
       */
      disableStatusChange: false,
      /**
       * @description Идентификатор причины отказа.
       * @type {number|null}
       */
      rejectionReasonId: null,
      /**
       * @description Уникальная причина отказа.
       * @type {string}
       */
      customRejectionReason: '',
      /**
       * @description Уникальная источник пациента.
       * @type {string}
       */
      customAdvertisingChannel: '',
      /**
       * @description Дата рождения.
       * @type {string}
       */
      birthAt: this.initialPatientBirthAt,
      /**
       * @description Причина отмены.
       * @type {string}
       */
      cause: '',
      /**
       * @description Вторичный прием.
       * @type {boolean}
       */
      isSecond: !this.initialIsInitial,
      /**
       * @description Поле пациента некорректно заполнено.
       * @type {boolean}
       */
      isPatientFieldInvalid: false,
      /**
       * @description Необходимо создать нового пациента.
       * @type {boolean}
       */
      needsCreatingPatient: this.initialNeedsCreatingPatient,
      /**
       * @description Имя.
       * @type {string}
       */
      name: this.initialPatientName,
      /**
       * @description Примечание.
       * @type {string}
       */
      note: this.initialNote,
      /**
       * @description Количество доступных записей листа ожидания.
       * @type {number}
       */
      numWaitings: this.waitingsIds.length,
      /**
       * @description Идентификатор пациента.
       * @type {null|number}
       */
      patientId: this.initialPatientId ?? this.suggestedPatientId,
      /**
       * @description Запрос на поиск пациента.
       * @type {string}
       */
      patientSearchQuery: '',
      /**
       * @description Отчество.
       * @type {string}
       */
      patronymic: this.initialPatientPatronymic,
      /**
       * @description Полное ФИО пациента.
       * @type {string}
       */
      patientFullName: this.initialPatientFullName,
      /**
       * @description Номер телефона.
       * @type {string}
       */
      phone: this.initialPatientPhone,
      /**
       * @description Тип номера телефона.
       * @type {string}
       */
      phoneType: this.initialPatientPhoneType,
      /**
       * @description Согласен на рассылку.
       * @type {boolean}
       */
      agreeMailing: true,
      /**
       * @description Поисковой запрос услуги.
       * @type {string}
       */
      searchServiceQuery: '',
      autocompleteKey: 0,
      /**
       * @description Список идентификаторов услуг.
       * @type {number[]}
       */
      serviceIds,
      /**
       * @description Фамилия.
       * @type {string}
       */
      surname: this.initialPatientSurname,
      isShownCanceledDialog: false,
      /**
       * @description Список пациентов (с возможностью фильтра)
       * @type {Object[]}
       */
      patients: this.patientList,
      selectedDoctorAssistantId,
      isShowDoctorAssistant,
    };
  },
  /**
   * @description Максимальная длина примечания.
   * @type {number}
   */
  maxNoteLength: 1000,
  methods: {
    onAddDoctorAssistant() {
      this.isShowDoctorAssistant = true;
    },
    onRemoveDoctorAssistant() {
      this.selectedDoctorAssistantId = null;
      this.isShowDoctorAssistant = false;
    },
    /**
     * Обработать событие закрытие окна отмены записи.
     * @returns {void}
     */
    onCloseCancelAppointmentDialog() {
      this.isShownCanceledDialog = false;
      this.cause = '';
      this.rejectionReasonId = null;
    },
    /**
     * Обработать событие закрытие окна создания канала.
     * @returns {void}
     */
    onCloseCreatingAdvertisingChannelDialog() {
      this.customAdvertisingChannel = '';
      this.advertisingChannelId = null;
    },
    /**
     * Показать сообщение об ошибки редактирования даты
     * @returns {void}
     */
    onShowFailMessageEditDate() {
      this.notifyFail('Редактирование даты состоявшегося визита невозможно');
    },
    /**
     * @description Установить идентификатор источника по названию агрегатора.
     * @returns {void}
     */
    setAdvertisingIdByAggregatorName() {
      const isSelected = this.advertisingChannelId !== 'new';
      if (isSelected) { return; }
      if (this.advertisingChannelList.length && this.customAdvertisingChannel) {
        const aggregatorName = this.customAdvertisingChannel.toLowerCase();
        const candidate = this
          .advertisingChannelList
          .find((channel) => channel.name.toLowerCase().includes(aggregatorName));
        this.advertisingChannelId = candidate?.id || null;
      }
    },
    /**
     * @description Обновить статус визит.
     * @returns {void}
     */
    updateStatus() {
      this.disableStatusChange = true;
      if (this.isConfirmed) {
        this.$emit('came', this.appointmentId);
      } else {
        this.$emit('confirm', this.appointmentId);
      }
    },
    /**
     * @description Установить данные нового пациента из запроса на поиск пациента.
     * @returns {void}
     */
    setNewPatientDataFromPatientSearchQuery() {
      if (this.patientSearchQuery === null) {
        return;
      }
      const [surname = '', name = '', patronymic = ''] = this.patientSearchQuery.split(' ');
      this.surname = surname;
      this.name = name;
      this.patronymic = patronymic;
    },
    /**
     * @description Установить пациента.
     * @param {Number} patientId Идентификатор пациента.
     * @returns {void}
     */
    setMatchedPatient(patientId) {
      this.patientId = patientId;
      this.isPatientFieldInvalid = false;
      this.needsCreatingPatient = false;
      this.$emit('clear-matched-patients');
    },
    /**
     * @description Отменить визит.
     * @returns {void}
     */
    cancelAppointment() {
      this.$emit('cancel', this.appointmentId, this.cause, this.rejectionReasonId, this.customRejectionReason);
    },
    /**
     * @description Создать источник пацинета.
     * @returns {void}
     */
    createAdvertisingChannel() {
      this.$emit('create-advertising-channel', this.customAdvertisingChannel);
    },
    /**
     * @description Проверить является ли день рабочим.
     * @param {string} date Дата.
     * @returns {boolean}
     */
    checkDayIsWork(date) {
      return this.employeeWorkDays.includes(date);
    },
    /**
     * @description Создать запрос назначения встречи с новым пациентом.
     * @returns {AppointmentNewPatientCreationRequest}
     */
    createNewPatientAppointmentRequest() {
      const birthDate = this.formatDateToISO(this.birthAt);
      return new AppointmentNewPatientCreationRequest(
        this.advertisingChannelId,
        birthDate,
        this.isoDate,
        this.initialEmployeeId,
        this.initialTimeEnds,
        this.isInitial,
        this.name,
        this.note,
        this.patronymic,
        this.phone,
        this.phoneType,
        this.serviceIds,
        this.initialTimeStarts,
        this.surname,
        this.agreeMailing,
        this.selectedDoctorAssistantId,
      );
    },
    /**
     * @description Создать запрос назначения встречи с существующим пациентом.
     * @returns {AppointmentExistPatientCreationRequest}
     */
    createExistPatientAppointmentRequest() {
      return new AppointmentExistPatientCreationRequest(
        this.advertisingChannelId,
        this.isoDate,
        this.initialEmployeeId,
        this.initialTimeEnds,
        this.isInitial,
        this.note,
        this.patientId,
        this.serviceIds,
        this.initialTimeStarts,
        this.waitingId,
        this.crmDealId,
        this.selectedDoctorAssistantId,
      );
    },
    /**
     * @description Необходимо ли извлечь список совпадающих пациентов.
     * @type {boolean}
     */
    isNeedFetchMatchedPatients() {
      return this.needsCreatingPatient && (this.isNameFilled || this.isPhoneValid);
    },
    /**
     * @description Необходимо ли запросить уведомления.
     * @returns {false|string}
     */
    isNeedFetchNotification() {
      return this.isEdit && this.patientId;
    },
    /**
     * @description Обработать потерю фокуса с поля выбора пациента.
     * @returns {void}
     */
    onBlurInputPatient() {
      this.isPatientFieldInvalid = !this.patientId && this.patientSearchQuery;
    },
    /**
     * @description Обработать удаление причины отказа.
     * @param {number} id
     */
    onDeleteRejectionReason(id) {
      this.$emit(NAME_EVENT_DELETE_REJECTION_REASON, id);
    },
    /**
     * @description Обработать поиск совпадающих пациентов.
     * @returns {void}
     */
    onMatchPatients() {
      const request = new GetMatchedPatientsRequest(
        this.isNameFilled ? this.name : '',
        this.isNameFilled ? this.patronymic : '',
        this.isPhoneValid ? [this.phone] : [],
        this.isNameFilled ? this.surname : '',
      );
      this.$emit(NAME_EVENT_MATCH_PATIENTS, request);
    },
    /**
     * @description Обработать изменение месяца.
     * @param {string} yearAndMonth Год и месяц.
     * @returns {void}
     */
    onChangeMonth(yearAndMonth) {
      this.$emit(NAME_EVENT_INPUT_MONTH, yearAndMonth);
    },
    /**
     * @description Обработать копирование данных записи на прием.
     * @returns {void}
     */
    onCopy() {
      const patientBirthDate = this.formatDateToISO(this.birthAt);
      const intervalDurationInMinutes = calcDifferenceMinutesForStringTime(
        this.initialTimeStarts,
        this.initialTimeEnds,
      );
      const appointment = {
        advertisingChannelId: this.advertisingChannelId,
        employeeId: this.initialEmployeeId,
        needsCreatingPatient: this.needsCreatingPatient,
        note: this.note,
        patientBirthDate,
        patientId: this.needsCreatingPatient ? null : this.patientId,
        patientName: this.initialPatientSurnameWithInitials,
        patientAge: this.initialPatientAge,
        patientPatronymic: this.patronymic,
        patientPhone: this.phone,
        patientSurname: this.surname,
        serviceIds: this.serviceIds.slice(),
        intervalDurationInMinutes,
        isInitial: this.isInitial,
        isCame: this.isCame,
      };
      this.$emit('copy', appointment);
      this.reachGoal();
    },
    /**
     * @description Достичь цели
     * @returns {void}
     */
    reachGoal() {
      this.$goal(GOAL_COPY_APPOINTMENT);
    },
    /**
     * @description Обработать закрытие формы.
     * @returns {void}
     */
    onClose() {
      this.$emit('close');
    },
    /**
     * @description Обработать отправку данных.
     * @returns {void}
     */
    onSubmit() {
      if (this.isDoctorUnavailable) {
        this.notifyFail(`У ${this.selectedEmployeeName} нет свободных слотов в выбранный день. Измените врача или дату приема.`);
        return;
      }
      if (this.needsCreatingPatient) {
        this.dispatchSubmitCreating();
      } else {
        this.dispatchSubmitEditing();
      }
    },
    /**
     * @description Обработать установку времени.
     * @param {string}   startsTime   Время начала.
     * @param {string}   endsTime     Время окончания.
     * @param {function} closePopover Закрыть окно.
     * @returns {void}
     */
    onSubmitTime(startsTime, endsTime, closePopover) {
      this.$emit('input-times', startsTime, endsTime);
      closePopover();
    },
    /**
     * @description Обработать восстановление записи на прием.
     * @returns {void}
     */
    onRestoreVisit() {
      this.$emit('restore', this.createExistPatientAppointmentRequest());
    },
    /**
     * @description Обработать переход к медицинской карте пациента
     * @returns {void}
     */
    async onPatientMedicalCard() {
      await this.$router.push({
        name: 'ClinicPanelPatientBasic',
        params: {
          id: this.initialPatientId,
        },
      });
    },
    /**
     * @description Обработать переход к счетам пациента
     * @returns {void}
     */
    async onPatientInvoices() {
      await this.$router.push({
        name: 'ClinicPanelPatientInvoiceList',
        params: {
          id: this.initialPatientId,
        },
      });
    },
    /**
     * @description Перейти к визиту пациента.
     * @returns {void}
     */
    openVisit() {
      this.$emit('open-visit');
    },
    /**
     * @description Обработать отправку открытия записей листа ожидания.
     * @returns {void}
     */
    openWaitings() {
      this.$emit('open-waitings', this.waitingsIds);
    },
    onUpdateServiceQuery(value) {
      this.searchServiceQuery = value;
    },
    /**
     * @description Обработать выбор услугу.
     */
    onSelectService() {
      this.searchServiceQuery = '';
      this.$nextTick(() => {
        this.autocompleteKey += 1;
      });
    },
    /**
     * @description Отправить событие отправки создания записи на прием.
     * @returns {void}
     */
    dispatchSubmitCreating() {
      const request = this.createNewPatientAppointmentRequest();
      this.$emit('submit-creating', request);
    },
    /**
     * @description Отправить событие отправки редактирования записи на прием.
     * @returns {void}
     */
    dispatchSubmitEditing() {
      if (!this.patientId) {
        this.notifyFail('Пациент не выбран');
        this.isPatientFieldInvalid = true;
        return;
      }
      const request = this.createExistPatientAppointmentRequest();
      this.$emit('submit-editing', request);
    },
    /**
     * @description Обработать ввод даты записи на прием.
     * @param {Date} value
     * @returns {void}
     */
    onInputDate(value) {
      if (this.initialDate.getTime() !== value.getTime()) {
        this.$emit(NAME_EVENT_INPUT_DATE, value);
        this.reloadTimeline(this.initialEmployeeId, this.formatDateToISO(value));
      }
    },
    /**
     * @description Обработать изменение сотрудника.
     * @param {number} value
     * @returns {void}
     */
    onInputEmployee(value) {
      if (this.selectedDoctorAssistantId === value) {
        this.selectedDoctorAssistantId = null;
      }
      this.$emit(NAME_EVENT_INPUT_EMPLOYEE, value);
      if (value) {
        this.reloadTimeline(value, this.isoDate);
      }
    },
    /**
     * @description Обработать ввод номера.
     * @param {{type:string,value:string}} event
     * @returns {void}
     */
    onInputPhone({ type, value }) {
      this.phone = value;
      this.phoneType = type;
    },
    /**
     * @description Обработать изменение пациента.
     * @param {?number} value
     * @returns {void}
     */
    onInputPatient(value) {
      this.patientId = value;
      this.onFetchNotification(value);
      if (value && this.isPatientFieldInvalid) {
        this.isPatientFieldInvalid = false;
      }
    },
    /**
     * Запросить уведомления
     *
     * @param {int} patientId
     * @returns {void}
     */
    onFetchNotification(patientId) {
      this.$emit(NAME_EVENT_FETCH_WAITING_PATIENT, patientId);
    },
    /**
     * @description Получить временную шкалу.
     * @param {number} employeeId
     * @param {string} isoDate
     * @returns {void}
     */
    reloadTimeline(employeeId, isoDate) {
      this.$emit('reload-timeline', employeeId, isoDate, this.initialTimeStarts);
    },
    /**
     * @description получить список пациентов, с возможностью фильтра
     * @param {string} value
     * @return {void}
     */
    fetchPatientList: debounce(function _(value = '') {
      this.$appointmentScheduleGateway.getPatientListFromAppointment(value)
        .then((patients) => {
          this.patients = patients.val;
        });
    }, 300),
  },
  mixins: [
    dateMixin,
    formMeaningfulFieldsMixin,
    notifierMixin,
    searchServiceMixin,
    searchPatientMixin,
  ],
  name: 'FormAppointmentCreation',
  props: {
    /**
     * @description Действия.
     */
    actions: { required: true, type: Array },
    /**
     * @description Список рекламных каналов.
     */
    advertisingChannelList: { type: Array, required: true },
    /**
     * @description Обязательность заполнения источника при создании нового пациента.
     */
    isAdvertisingChannelRequired: { type: Boolean, default: false },
    /**
     * @description Список причин отказа.
     */
    rejectionReasonList: { type: Array, required: true },
    /**
     * @description Имя агрегатора для выявления источника.
     */
    aggregatorName: { type: String, default: () => '' },
    /**
     * @description Идентификатор записи на прием.
     */
    appointmentId: { default: () => null, type: Number },
    /**
     * @description Статус записи на прием.
     */
    appointmentStatus: { default: () => '', type: String },
    /**
     * @description Идентификатор кабинета.
     */
    cabinetId: { default: () => null, type: Number },
    /**
     * @description Может менять пациента.
     */
    canChangePatient: { default: () => true, type: Boolean },
    /**
     * @description Может создавать пациентов.
     */
    canCreatePatients: { default: () => false, type: Boolean },
    /**
     * @description Может изменять первичность визита.
     */
    canEditInitial: { default: () => false, type: Boolean },
    /**
     * @description Список сотрудников.
     */
    employeeList: { default: () => [], type: Array },
    /**
     * @description Временная шкала сотрудника.
     */
    employeeTimeline: { default: () => [], type: [Object, Array] },
    /**
     * @description Рабочие дни сотрудника.
     */
    employeeWorkDays: { default: () => [], type: Array },
    /**
     * @description Список врачей-ассистентов.
     */
    doctorAssistantList: { default: () => [], type: Array },
    /**
     * @description Идентификатор рекламного канала.
     */
    initialAdvertisingChannelId: { default: () => null, type: Number },
    /**
     * @description Название рекламного канала.
     */
    initialAdvertisingChannelName: { default: () => null, type: String },
    /**
     * @description Дата.
     */
    initialDate: { default: () => null, required: true, type: Date },
    /**
     * @description Врач-ассистент.
     */
    initialDoctorAssistant: { default: () => null, type: Object },
    /**
     * @description Идентификатор сотрудника.
     */
    initialEmployeeId: { default: () => null, type: Number },
    /**
     * @description Ожидание завершения запроса.
     */
    isAwaitRequest: { required: true, type: Boolean },
    /**
     * @description Заявка устарела.
     */
    isOutdated: { default: () => false, type: Boolean },
    /**
     * @description Состояние деактивации поля.
     */
    isDisabledField: { default: () => false, type: Boolean },
    /**
     * @description Изначально отобразить создание пациента.
     */
    initialNeedsCreatingPatient: { default: () => false, type: Boolean },
    /**
     * @description Примечание.
     */
    initialNote: { default: () => '', type: String },
    /**
     * @description Дата рождения пациента.
     */
    initialPatientBirthAt: { default: () => '', type: String },
    /**
     * @description Идентификатор пациента.
     */
    initialPatientId: { default: () => null, type: Number },
    /**
     * @description Черты пациента.
     */
    initialPatientLabels: { default: () => [], type: Array },
    /**
     * @description Имя пациента.
     */
    initialPatientName: { default: () => '', type: String },
    /**
     * @description Фамилия пациента.
     */
    initialPatientSurname: { default: () => '', type: String },
    /**
     * @description Отчество пациента.
     */
    initialPatientPatronymic: { default: () => '', type: String },
    /**
     * @description Полное ФИО пациента.
     */
    initialPatientFullName: { default: () => '', type: String },
    /**
     * @description Фамилия и инициалы пациента.
     */
    initialPatientSurnameWithInitials: { default: () => '', type: String },
    /**
     * @description Возраст пациента.
     */
    initialPatientAge: { default: () => '', type: String },
    /**
     * @description Телефон пациента.
     */
    initialPatientPhone: { default: () => '', type: String },
    /**
     * @description Тип номера телефона пациента.
     */
    initialPatientPhoneType: { default: () => PHONE_TYPE_MOBILE, type: String },
    /**
     * @description Услуги.
     */
    initialServices: { default: () => [], type: Array },
    /**
     * @description Время окончания.
     */
    initialTimeEnds: { default: () => '', type: String },
    /**
     * @description Время начала.
     */
    initialTimeStarts: { default: () => '', type: String },
    /**
     * @description Первичный.
     */
    initialIsInitial: { default: () => false, type: Boolean },
    /**
     * @description Нарушения ограничений.
     */
    inputErrors: { required: true, type: Object },
    /**
     * @description Список совпадающих пациентов.
     */
    matchedPatients: { default: () => [], type: [Object, Array] },
    /**
     * @description Список пациентов.
     */
    patientList: { default: () => [] },
    /**
     * @description Предлагаемый идентификатор пациента.
     */
    suggestedPatientId: { default: () => null, type: Number },
    /**
     * @description Идентификатор CRM-сделки по которой создается прием.
     */
    crmDealId: { default: () => null, type: Number },
    /**
     * @description Список заметок по визиту.
     */
    visitNotes: { default: () => [], type: Array },
    /**
     * @description Кол-во непрочитанных заметок по визиту.
     */
    unreadVisitNotesCount: { default: () => 0, type: Number },
    /**
     * @description Идентификаторы записей из листа ожидания..
     */
    waitingsIds: { default: () => [], required: false, type: Array },
    /**
     * @description Идентификатор записи из листа ожидания.
     */
    waitingId: { default: () => 0, required: false, type: Number },
    /**
     * @description Возможность копирования записи.
     */
    hasCopy: { default: false, type: Boolean },
    /**
     * @description Показывать кнопки обновления статуса.
     */
    showUpdateStatusButtons: { default: false, type: Boolean },
    /**
     * @description Может просматривать мед. карту пациента.
     * @type {boolean}
     */
    hasViewPatientMedicalCard: { default: false, type: Boolean },
    /**
     * @description Может просматривать визит пациента.
     * @type {boolean}
     */
    hasViewPatientVisit: { default: true, type: Boolean },
    /**
     * @description Может просматривать счета пациента.
     * @type {boolean}
     */
    hasViewPatientInvoices: { default: false, type: Boolean },
    /**
     * Нотификация о нахождении пациента в листе ожидания
     * @type {object}
     */
    notification: { default: () => null, type: Object },
    /**
     * Новый визит
     * @type {Boolean}
     */
    isSelectedAppointments: { required: true, type: Boolean },
    /**
     * @description Заданные продолжительности.
     * @type {Array}
     */
    presetsDuration: { default: () => [], type: Array },
  },
  watch: {
    advertisingChannelList: {
      handler: 'setAdvertisingIdByAggregatorName',
      immediate: true,
    },
    stringifiedNewPatientData: {
      handler: debounce(function tryFetchingMatchedPatients() {
        if (this.isNeedFetchMatchedPatients()) {
          this.onMatchPatients();
        }
      }, 500),
    },
    needsCreatingPatient(updatedValue) {
      if (updatedValue) {
        this.setNewPatientDataFromPatientSearchQuery();
      }
    },
    patient: {
      handler(value) {
        if (value && value.advertisingChannelId > -1) {
          this.advertisingChannelId = value.advertisingChannelId;
        }
        if (!this.canEditInitial) {
          this.isSecond = !!value && (value.visitCount !== 0 || value.notInitial);
        }
      },
      immediate: true,
    },
    isAwaitRequest: {
      handler(value) {
        if (value !== true) {
          this.disableStatusChange = false;
        }
      },
    },
    patientSearchQuery: {
      handler(newValue, oldValue) {
        if ((newValue || newValue === '') && oldValue) {
          const query = this.prepareQuery(newValue, oldValue);
          if (query === null) {
            return;
          }
          this.fetchPatientList(query);
        }
      },
    },
  },
};
</script>
<style lang="scss">
@use "@/scss/buttons";

.v-dialog {
  &.appointment-creation__popup {
    overflow-y: visible;
  }
  .appointment-creation {
    &__patient {
      margin-bottom: 20px;
    }
    &__popup {
      &__button-wrapper.v-card__actions {
        display: flex;
        padding: 16px 16px !important;
      }
      &__button-cancel.v-btn {
        @include buttons.btn-transparent(10rem, 3rem, 1rem);
      }
      &__button-accept.v-btn {
        @include buttons.btn-blue(10rem, 3rem, 1rem);
      }
      &__button-cancel.v-btn,
      &__button-accept.v-btn {
        font-weight: 400;
        letter-spacing: normal;
        text-transform: none;
        transition: none;
      }
    }
  }
}
.theme--light.v-text-field > .v-input__control > .v-input__slot:before {
  border-color: #c2cfe0;
}
.v-application p {
  margin-bottom: 0;
}
.v-chip .v-chip__content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: auto;
  display: block;
  padding: 0 20px 0 0;
}
.v-application--is-ltr .v-chip .v-chip__close.v-icon.v-icon--right {
  margin-right: -4px;
  position: absolute;
  right: 12px;
}
.appointment-creation {
  margin: 40px 0 0 0;
  display: flex;
  flex-direction: column;
  & &__phone {
    flex-direction: row;
    align-items: center;
    .checkbox {
      margin-top: 18px;
    }
  }
  &__date {
    margin-top: 23px;
    max-width: 125px;
  }
  &__checkboxes {
    width: fit-content;
  }
  &__mailing {
    margin-top: 18px;
  }
  &__popup {
    &__exit {
      position: absolute;
      right: 5px;
      top: 5px;
    }
  }
  &__info {
    &-duration {
      display: flex;
      font-size: 16px;
      line-height: 25px;
      margin: 10px 0 0 0;
      &::before {
        content: "";
        background-image: url("@/assets/img/error.svg");
        background-repeat: no-repeat;
        background-size: contain;
        width: 24px;
        height: 24px;
        margin: 0 5px 0 0;
      }
    }
  }
  &__multiline {
    margin: 10px 0 0 0;
    &::v-deep .v-chip--select {
      white-space: pre-line !important;
      min-height: 30px;
      width: 100%;
      height: auto !important;
      & > .v-chip__content {
        justify-content: space-between;
        width: 100%;
      }
    }
    &::v-deep .v-list-item__title {
      white-space: pre-line;
    }
    &.label.v-label.v-label--active.theme--light {
      top: 2px;
    }
    &.v-select.v-select--chips .v-select__selections {
      min-height: 34px;
    }
  }
  &__datetime {
    display: flex;
    align-items: center;
    gap: 20px;
    &__value {
      border-bottom: 1px dashed;
      color: var(--action-color);
      cursor: pointer;
      font-size: 18px;
      font-weight: 500;
      justify-self: center;
      line-height: 22px;
      white-space: nowrap;
      &__disabled {
        font-size: 18px;
        font-weight: 400;
        cursor: not-allowed;
        border-bottom: 0;
      }
      &:hover:not(:disabled) {
        border-bottom: 1px dashed transparent;
      }
      &:disabled {
        font-weight: 400;
        color: inherit;
        cursor: not-allowed;
        border-bottom: 0;
      }
      &--error {
        color: #F5222D;
        border-bottom-color: #F5222D;
      }
    }
    &__error {
      font-size: 12px;
      color: var(--warning-color);
    }
  }
  &__outdated {
    color: var(--unfocus-color);
    font-weight: 700;
  }
  &__double {
    display: flex;
    flex-direction: column;
    div {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      margin: 5px 0 0 0;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
    }
    &__title {
      color: red;
    }
    &__error:hover {
      color: #0389e0;
    }
    &__icon-archive {
      display: inline-block;
      width: 16px;
      height: 16px;
      background-image: url("@/assets/img/archive-add-blue.svg");
      background-repeat: no-repeat;
      background-size: contain;
      background-position: center;
    }
  }
  &__fields {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    margin-block: 10px 26px;
  }
  &__doctor {
    display: flex;
    flex-direction: row;
    grid-gap: 15px;
    font-weight: 400;
    color: #192a3e;
    font-size: 16px;
    line-height: 19px;
    align-items: flex-start;
    width: 100%;
    dt {
      margin-top: 25px;
    }
    &__name {
      flex: 1;
      max-width: 250px;
      min-width: 160px;
      font-weight: 600;
    }
    &__button {
      height: 1.5rem;
      width: 1.5rem;
      margin: auto 0 5px;
      &.add {
        background: no-repeat center url('@/assets/img/plus.svg');
      }
      &.remove {
        background: no-repeat center url('@/assets/img/minus-rounded-blue.svg');
      }
    }
  }
  &__error-notification {
    color: #F5222D;
    font-size: 12px;
    margin-top: 4px;
  }
  &__patient {
    width: 100%;
    margin: 10px 0 0 0;
    &__labels {
      flex-wrap: wrap;
    }
  }
  &__wrapper {
    margin-top: 23px;
  }
  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 10px;
    margin: 23px 0 0 0;
    &.extra {
      grid-template-columns: auto 1fr;
      align-items: center;
      margin: 23px 0 0 0;
    }
  }
  &__waiting-list-notification {
    display: flex;
    gap: 20px;
    text-align: left;
  }
  &__waiting-list-link {
    text-decoration: none;
  }
  &__textarea {
    margin: 34px 0 0 0;
  }
  &__notes {
    margin-top: 20px;
  }
  &__buttons {
    display: grid;
    gap: 10px;
    grid-template-columns: 1fr 1fr;
    margin: 20px 0 0 0;
    width: 100%;
    &.with-copy {
      grid-template-columns: 70px 1fr 1fr;
    }
  }
  &__button {
    @include buttons.btn-transparent(100%, 56px, 20px);
    &-cancel {
      grid-column-start: -3;
      & > img {
        width: 20px;
        margin-right: 7px;
      }
    }
    &-save,
    &-accept,
    &-visit {
      @include buttons.btn-blue(100%, 56px, 20px);
      & > img {
        width: 20px;
        margin-right: 7px;
      }
      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
    &-accept {
      background-color: #2bac6e;
      &:hover {
        background-color: #00954d;
      }
    }
    &-save {
      grid-column-start: -2;
    }
    &-copy {
      background-size: 26px 26px;
      background-repeat: no-repeat;
      background-position: center;
      background-image: url("@/assets/img/copy-appointment-blue.svg");
      @include buttons.btn-transparent(100%, 56px, 20px);
      &:disabled {
        border-color: var(--unfocus-color);
        background-image: url("@/assets/img/copy-appointment-grey.svg");
      }
    }
  }
}

.appointment-notes {
  &__header {
    align-items: center;
    column-gap: 4px;
    padding-inline: 0;
    padding-block-end: 25px;
    .v-expansion-panel-header__icon {
      margin-left: 0;
    }
  }
  &__content {
    .v-expansion-panel-content__wrap {
      padding-inline: 0;
    }
  }
  & &__title {
    flex-grow: 0;
    font-size: 20px;
    font-weight: 600;
  }
  &__count {
    font-weight: 400;
    color: #F5222D;
    cursor: help;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
  }
  &__item {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 250px;
    padding: 14px 8px 8px;
    background: #FFFFFF;
    border-radius: 10px;
    border: 1px solid #c2cfe0;
    overflow-wrap: anywhere;
    overflow: auto;
    &--new {
      background: #0091E91A;
    }
    &__content {
      font-weight: 400;
      font-size: 16px;
      color: #000000;
      white-space: pre-wrap;
      p {
        margin: 0;
      }
    }
    &__info {
      display: flex;
      gap: 6px;
      font-size: 12px;
      font-weight: 400;
      color: #929FB3;
    }
  }
}

.open-waiting-list {
  align-self: baseline;
  color: var(--action-color);
  margin-left: auto;
  white-space: nowrap;
}
@media screen and (max-width: 640px) {
  .appointment-creation {
    &__row {
      grid-template-columns: 1fr;
      grid-gap: 23px;
      &.extra {
        grid-template-columns: 1fr;
      }
    }
    & &__phone {
      flex-direction: column;
      align-items: stretch;
      .checkbox {
        margin-top: 0;
      }
    }
    &__mailing {
      margin-top: 0;
    }
    &__date {
      max-width: none;
    }
    &__button {
      &-cancel > img,
      &-save > img {
        display: none;
      }
    }
    &__buttons, &__buttons.with-copy {
      grid-template-columns: 1fr;
    }
  }
}

@media screen and (max-width: 400px)  {
  .v-dialog {
    .appointment-creation {
      &__popup {
        &__button-wrapper.v-card__actions {
          flex-direction: column;
          row-gap: 10px;
          .v-btn {
            width: 100%;
          }
        }
        &__button-accept.v-btn {
          margin-left: 0 !important;
        }
      }
    }
  }
  .appointment-creation {
    &__doctor {
      &__name {
        max-width: 100%;
      }
    }
  }
}
</style>
